<script setup lang="ts">
import type Collection from '@/core/resources/Collection'
import GenericTable from '@/components/GenericTable.vue'
import CollectionTableLine from '@/components/tables/CollectionTableLine.vue'
import { computed, onMounted, ref, watch } from 'vue'
import { collectionService } from '@/core/services'
import { useStore } from '@/core/store'
import { handleError } from '@/utils/ErrorHandler'
import { flashMessage } from '@/utils/FlashMessages'
import { VisibilitySpectrum, Accessibility } from '@/core/Common'
import { popupUtil } from '@/utils/Popup'

const fetchCollections = () => {
  collectionService
    .getAll(store.getAccessToken())
    .then((collections) => {
      collectionList.value = collections

      selectedMap.value = {}
      collections.forEach((c) => {
        selectedMap.value[c.name] = false
      })
      allSelected.value = false
    })
    .catch(handleError)
}

const deleteCollections = async () => {
  const confirmed = await popupUtil.confirm('Are you sure you want to delete the selected collections?')
  if (!confirmed) return

  collectionService
    .deleteMany(selectedCollections(), store.getAccessToken())
    .then(() => {
      flashMessage.success('Collections deleted successfully')
      fetchCollections()
    })
    .catch(handleError)
}

const selectedCollections = () => {
  return collectionList.value.filter((collection) => selectedMap.value[collection.name])
}

const updateCollectionsVisibility = async () => {
  const confirmed = await popupUtil.confirm('Are you sure you want to change the visibility of the selected collections?')
  if (!confirmed) {
    selectedVisibility.value = ''
    return
  }

  collectionService
    .updateVisibility(
      selectedCollections().map((collection) => collection.name),
      VisibilitySpectrum[selectedVisibility.value as keyof typeof VisibilitySpectrum],
      store.getAccessToken()
    )
    .then(() => {
      fetchCollections()
    })
    .catch(handleError)

  selectedVisibility.value = ''
}

const updateCollectionsAccessibility = async () => {
  const confirmed = await popupUtil.confirm('Are you sure you want to change the accessibility of the selected collections?')
  if (!confirmed) {
    selectedAccessibility.value = ''
    return
  }

  collectionService
    .updateAccessibility(
      selectedCollections().map((collection) => collection.name),
      Accessibility[selectedAccessibility.value as keyof typeof Accessibility],
      store.getAccessToken()
    )
    .then(() => {
      fetchCollections()
    })
    .catch(handleError)

  selectedAccessibility.value = ''
}

const exportCollections = async () => {
  const confirmed = await popupUtil.confirm(
    'You are going to build the archive of the selected collections and update minmax values. This can take a few minutes. Are you sure you want to proceed?'
  )

  if (!confirmed) return

  Promise.all(
    selectedCollections().map((collection) => {
      return collectionService.exportCollection(collection, store.getAccessToken())
    })
  )
    .then(() => {
      flashMessage.success('Building archives successfully launched')
    })
    .catch(handleError)
}

const exportCollection = async (collection: Collection) => {
  const confirmed = await popupUtil.confirm(
    'You are going to build the archive of the selected collection and update minmax values. This can take a few minutes. Are you sure you want to proceed?'
  )

  if (!confirmed) {
    return
  }

  collectionService
    .exportCollection(collection, store.getAccessToken())
    .then(() => {
      flashMessage.success('Building archive successfully launched')
    })
    .catch(handleError)
}

const allSelected = ref(false)
const store = useStore()
const collectionList = ref<Collection[]>([])
const selectedMap = ref<{ [key: string]: boolean }>({})
const search = ref('')
const selectedVisibility = ref('')
const selectedAccessibility = ref('')
const sortParams = ref({ key: '', desc: false })
const headers = [
  { key: 'name', label: 'Name' },
  { key: 'visibility', label: 'Visibility' },
  { key: 'accessibility', label: 'Accessibility' },
  { key: 'role', label: 'Role' },
  { key: 'updated_at', label: 'Last changes at:' },
  { key: 'created_at', label: 'Created at:' },
  { key: 'action', label: 'Action' }
]

const sortedCollectionsList = computed(() => {
  let copy: Collection[] = []

  if (search.value) copy = collectionList.value.filter((collection) => collection.name.toLowerCase().includes(search.value.toLowerCase()))
  else copy = collectionList.value.slice()

  copy.sort((a, b) => {
    switch (sortParams.value.key) {
      case 'name':
        return a.name.localeCompare(b.name)
      case 'visibility':
        return a.visibility.localeCompare(b.visibility)
      case 'accessibility':
        return a.accessibility.localeCompare(b.accessibility)
      case 'role':
        return (a.role ?? '').localeCompare(b.role ?? '')
      case 'updated_at':
        return a.updateDate.getTime() - b.updateDate.getTime()
      case 'created_at':
        return a.createDate.getTime() - b.createDate.getTime()
      default:
        return 0
    }
  })

  return sortParams.value.desc ? copy.reverse() : copy
})

onMounted(() => {
  fetchCollections()
})

watch(allSelected, (value) => {
  for (const key in selectedMap.value) {
    selectedMap.value[key] = value
  }
})
</script>

<template>
  <div class="container">
    <h1>List of collections</h1>
    <div class="toolbar">
      <router-link to="/collections/new" class="button" id="new-collection-button">
        <span class="material-symbols-outlined">add</span>
        <span>New</span>
      </router-link>

      <button :disabled="selectedCollections().length === 0" @click="deleteCollections" id="delete-collections">
        <span class="material-symbols-outlined">delete</span>
        <span>Delete</span>
      </button>

      <button @click="fetchCollections" id="refresh-button">
        <span class="material-symbols-outlined">refresh</span>
        <span>Refresh</span>
      </button>

      <button :disabled="selectedCollections().length === 0" @click="exportCollections" id="export-button">
        <span class="material-symbols-outlined">exit_to_app</span>
        <span>Build archive</span>
      </button>

      <span> Change visibility: </span>

      <select
        id="visibility-select"
        :disabled="selectedCollections().length === 0"
        v-model="selectedVisibility"
        @change="updateCollectionsVisibility">
        <option disabled value="">Select visibility</option>
        <option value="DISPLAY">Published</option>
        <option value="READY">Ready to publish</option>
        <option value="HIDDEN">Hidden</option>
      </select>

      <span> Change accessibility: </span>

      <select
        id="accessibility-select"
        :disabled="selectedCollections().length === 0"
        v-model="selectedAccessibility"
        @change="updateCollectionsAccessibility">
        <option disabled value="">Select accessibility</option>
        <option value="PUBLIC">Public</option>
        <option value="PRIVATE">Private</option>
      </select>

      <div class="search-bar">
        <span class="material-symbols-outlined search-icon">search</span>
        <input type="text" v-model="search" placeholder="Search..." />
      </div>
    </div>

    <GenericTable :headers="headers" v-model:sortParams="sortParams" v-model:checked="allSelected" @fetch="fetchCollections">
      <CollectionTableLine
        v-for="collection in sortedCollectionsList"
        :key="collection.name"
        :collection="collection"
        v-model="selectedMap[collection.name]"
        @export="exportCollection" />
    </GenericTable>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/styles/table.scss';
</style>
